(() => {
  const drawers = new WeakSet();
  const undoDuration = 8000;

  function getToast(drawer) {
    return drawer.querySelector('[data-cart-drawer-undo]');
  }

  function propertyEntries(properties) {
    if (Array.isArray(properties)) {
      return properties.map(property => Array.isArray(property)
        ? property
        : [property.name ?? property.first, property.value ?? property.last]);
    }
    return Object.entries(properties || {});
  }

  function rememberItem(state, item, keyboard = false) {
    if (!item?.dataset.key || item.hasAttribute('is-disabled')) return;

    try {
      const variantId = Number(item.dataset.variantId);
      const quantity = Number(item.dataset.quantity);
      if (!variantId || !quantity) return;

      state.pending = {
        key: item.dataset.key,
        id: variantId,
        quantity,
        properties: JSON.parse(item.dataset.undoProperties || '{}') || {},
        sellingPlan: item.dataset.undoSellingPlan,
        keyboard,
      };
    } catch {
      state.pending = null;
    }
  }

  function initDrawer(drawer) {
    if (drawers.has(drawer)) return;
    drawers.add(drawer);

    const state = { pending: null, removed: null, timer: null, hideTimer: null, restoring: false };

    function clearEmptyState() {
      drawer.setPreEmpty(false);
      drawer.removeAttribute('is-empty');
      drawer.removeAttribute('empty-cart');
      drawer.setAttribute('has-aside-products', '');
    }

    function dismiss() {
      clearTimeout(state.timer);
      state.removed = null;
      const toast = getToast(drawer);
      if (!toast) return;
      toast.classList.remove('is-visible');
      state.hideTimer = setTimeout(() => {
        if (!toast.classList.contains('is-visible')) toast.hidden = true;
      }, 220);
    }

    function scheduleDismiss(delay = undoDuration) {
      clearTimeout(state.timer);
      const toast = getToast(drawer);
      if (!state.removed || state.restoring || toast?.matches(':hover') || toast?.contains(document.activeElement)) return;
      state.timer = setTimeout(dismiss, delay);
    }

    function showUndo(removed) {
      const toast = getToast(drawer);
      if (!toast) return;

      clearTimeout(state.hideTimer);
      const message = toast.querySelector('[data-cart-drawer-undo-message]');
      const button = toast.querySelector('[data-cart-drawer-undo-button]');
      message.textContent = 'Removed from cart';
      button.textContent = 'Undo';
      button.dataset.undoAction = 'restore';
      button.disabled = false;
      toast.hidden = false;

      if (!toast.dataset.undoBound) {
        toast.dataset.undoBound = 'true';
        toast.addEventListener('mouseenter', () => clearTimeout(state.timer));
        toast.addEventListener('mouseleave', () => scheduleDismiss(5000));
        toast.addEventListener('focusin', () => clearTimeout(state.timer));
        toast.addEventListener('focusout', event => {
          if (!toast.contains(event.relatedTarget)) scheduleDismiss(5000);
        });
      }

      // Commit the initial style so the entrance transition runs after display changes.
      toast.getBoundingClientRect();
      requestAnimationFrame(() => {
        if (!toast.isConnected) return;
        toast.classList.add('is-visible');
        if (removed?.keyboard) button.focus();
      });
      scheduleDismiss();
    }

    async function restoreItem() {
      if (!state.removed || state.restoring) return;
      const item = state.removed;
      const toast = getToast(drawer);
      const button = toast?.querySelector('[data-cart-drawer-undo-button]');
      const message = toast?.querySelector('[data-cart-drawer-undo-message]');
      if (!button || !message) return;

      state.restoring = true;
      clearTimeout(state.timer);
      button.disabled = true;
      message.textContent = 'Restoring item...';

      const formData = new FormData();
      formData.append('id', String(item.id));
      formData.append('quantity', String(item.quantity));
      for (const [name, value] of propertyEntries(item.properties)) {
        if (name && value != null) formData.append(`properties[${name}]`, String(value));
      }
      if (item.sellingPlan) formData.append('selling_plan', item.sellingPlan);
      formData.append('sections', drawer.dataset.sectionId);
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || !result || result.description) {
          const description = typeof result?.description === 'string' ? result.description : 'Could not restore item';
          const error = new Error(description);
          error.retryable = response.status >= 500;
          throw error;
        }
        dismiss();
        clearEmptyState();
        drawer.update();
      } catch (error) {
        message.textContent = error.message || 'Could not restore item';
        button.textContent = error.retryable === false ? 'Close' : 'Retry';
        button.dataset.undoAction = error.retryable === false ? 'dismiss' : 'restore';
        button.disabled = false;
      } finally {
        state.restoring = false;
        if (state.removed) scheduleDismiss();
      }
    }

    drawer.addEventListener('click', event => {
      const removeButton = event.target.closest('cart-remove-button');
      if (!removeButton || drawer.classList.contains('is-cart-loading')) return;
      rememberItem(state, removeButton.closest('cart-item'));
    }, true);

    drawer.addEventListener('keydown', event => {
      if (event.key !== 'Enter' || drawer.classList.contains('is-cart-loading')) return;
      const removeButton = event.target.closest('cart-remove-button');
      if (removeButton) rememberItem(state, removeButton.closest('cart-item'), true);
    }, true);

    drawer.addEventListener('change', event => {
      if (!event.target.matches('[data-quantity-input]')) return;
      const item = event.target.closest('cart-item');
      if (Number(event.target.value) === 0) rememberItem(state, item);
      else if (state.pending?.key === item?.dataset.key) state.pending = null;
    }, true);

    drawer.addEventListener('click', event => {
      const button = event.target.closest('[data-cart-drawer-undo-button]');
      if (!button) return;
      if (button.dataset.undoAction === 'dismiss') dismiss();
      else restoreItem();
    });

    drawer.on('update-nodes', ({ node }) => {
      const updatedItems = node ? Array.from(node.querySelectorAll('cart-item')) : [];
      if (updatedItems.length) {
        clearEmptyState();
        const updatedKeys = new Set(updatedItems.map(item => item.dataset.key));
        requestAnimationFrame(() => {
          drawer.querySelectorAll('cart-item').forEach(item => {
            if (updatedKeys.has(item.dataset.key)
              && !item.hasAttribute('is-new')
              && item.style.maxHeight === '0px'
              && item.scrollHeight > 0) {
              item.setHeight(item.scrollHeight);
            }
          });
        });
      }

      const pending = state.pending;
      state.pending = null;
      if (pending && node) {
        const stillInCart = updatedItems.some(item => item.dataset.key === pending.key);
        if (!stillInCart) state.removed = pending;
      }
      if (state.removed && !state.restoring) queueMicrotask(() => showUndo(state.removed));
    });
  }

  function init() {
    document.querySelectorAll('cart-drawer').forEach(initDrawer);
  }

  customElements.whenDefined('cart-drawer').then(init);
  document.addEventListener('shopify:section:load', () => customElements.whenDefined('cart-drawer').then(init));
})();
